import React, { useState } from "react";
import {
  ChevronDown,
  ChevronUp,
  Phone,
  Mail,
  MessageSquare,
  Send,
  CheckCircle,
  HelpCircle,
  Wifi,
  Calendar,
  CreditCard,
  Users,
  Building,
  Bell,
} from "lucide-react";
import { useApp } from "../contexts/AppContext";
import { api } from "../services/api";
import "./HelpSupport.css";

const FAQ_DATA = [
  {
    id: 1,
    category: "General Questions",
    icon: <HelpCircle size={18} />,
    questions: [
      {
        q: "How do I find my session or venue?",
        a: "Session locations are mentioned under each session in the Programme section of the app.",
      },
      {
        q: "How do I ask questions to speakers or panelists?",
        a: "Please use the Session Questions button available on the respective session page.",
      },
      {
        q: "Where can I provide feedback for a session?",
        a: "You can share your feedback using the Survey button available on each session page.",
      },
      {
        q: "How do I access Masterclasses or Learning Labs?",
        a: "Details are available under the Programme section. Entry is subject to session capacity.",
      },
      {
        q: "Are school tours open to all participants?",
        a: "School tours are optional and available for teachers only. Please check availability at the venue.",
      },
      {
        q: "What should I do if I face a technical issue with the mobile app?",
        a: "Please contact the Support Team via WhatsApp or Email for immediate assistance.",
      },
      {
        q: "What should I do if I lose my badge or need on-site help?",
        a: "Please reach out to the Support Team through the app or visit the Help Desk at the venue.",
      },
    ],
  },
];

function HelpSupport() {
  const { showToast } = useApp();
  const [expandedCategory, setExpandedCategory] = useState(null);
  const [expandedQuestion, setExpandedQuestion] = useState(null);
  const [showQueryForm, setShowQueryForm] = useState(false);
  const [querySubmitting, setQuerySubmitting] = useState(false);
  const [querySubmitted, setQuerySubmitted] = useState(false);
  const [query, setQuery] = useState({
    subject: "",
    message: "",
  });

  const toggleCategory = (categoryId) => {
    setExpandedCategory(expandedCategory === categoryId ? null : categoryId);
    setExpandedQuestion(null);
  };

  const toggleQuestion = (questionIndex) => {
    setExpandedQuestion(
      expandedQuestion === questionIndex ? null : questionIndex,
    );
  };

  const handleQuerySubmit = async (e) => {
    e.preventDefault();

    if (!query.subject.trim() || !query.message.trim()) {
      showToast("Please fill in all fields", "error");
      return;
    }

    setQuerySubmitting(true);
    try {
      await api.submitSupportQuery(query);
      setQuerySubmitted(true);
      setQuery({ subject: "", message: "" });
      showToast("Query submitted successfully", "success");
    } catch (error) {
      console.error("Failed to submit query:", error);
      showToast("Failed to submit query. Please try again.", "error");
    } finally {
      setQuerySubmitting(false);
    }
  };

  const handleNewQuery = () => {
    setQuerySubmitted(false);
    setShowQueryForm(true);
  };

  return (
    <div className="help-page">
      {/* Header Info */}
      <div className="help-header">
        <div className="help-icon">
          <HelpCircle size={32} />
        </div>
        <h1>Help & Support</h1>
        <p>Find answers to common questions or contact our support team</p>
      </div>

      {/* Optional Live Nudges */}
      {/* <section className="help-section">
        <h2 className="section-title">
          <Bell size={18} />
          Optional Live Nudges (In-App)
        </h2>
        <div className="nudge-card">
          <div className="nudge-item">
            <Bell size={16} className="nudge-icon" />
            <p>
              Questions are open for the ongoing panel. Tap "Session Questions".
            </p>
          </div>
          <div className="nudge-item">
            <Bell size={16} className="nudge-icon" />
            <p>Share your feedback for this session under "Survey".</p>
          </div>
        </div>
      </section> */}

      {/* Support & Help */}
      <section className="help-section">
        <h2 className="section-title">
          <MessageSquare size={18} />
          Support & Help
        </h2>
        <p className="support-intro">
          For any assistance during the conference, please reach out to the
          Support Team using the options below.
        </p>

        <div className="support-contact-cards">
          <div className="support-card whatsapp">
            <div className="support-card-header">
              <Phone size={20} />
              <h3>For any access or registration issues, please contact:</h3>
            </div>
            <p className="contact-availability contact-number">
              Help Line 1 - +91 8928274246
            </p>
            <p className="contact-availability contact-number">
              Help Line 2 - +91 8591909523
            </p>
            <p className="contact-availability">
              Available during conference hours
            </p>
            <p className="contact-time">
              9:00 AM - 6:00 PM
            </p>
          </div>

          {/* <div className="support-card email">
            <div className="support-card-header">
              <Mail size={20} />
              <h3>Email Support</h3>
            </div>
            <p className="contact-email">
              support.bff2026@reliancefoundation.org
            </p>
          </div> */}
        </div>
      </section>

      {/* FAQs */}
      <section className="help-section">
        <h2 className="section-title">
          <MessageSquare size={18} />
          Frequently Asked Questions
        </h2>
        <div className="faq-list">
          {FAQ_DATA.map((category) => (
            <div key={category.id} className="faq-category">
              <button
                className={`category-header ${expandedCategory === category.id ? "expanded" : ""}`}
                onClick={() => toggleCategory(category.id)}
              >
                <div className="category-icon">{category.icon}</div>
                <span className="category-title">{category.category}</span>
                <span className="category-count">
                  {category.questions.length}
                </span>
                {expandedCategory === category.id ? (
                  <ChevronUp size={20} />
                ) : (
                  <ChevronDown size={20} />
                )}
              </button>

              {expandedCategory === category.id && (
                <div className="questions-list">
                  {category.questions.map((item, qIndex) => {
                    const questionId = `${category.id}-${qIndex}`;
                    return (
                      <div key={qIndex} className="question-item">
                        <button
                          className={`question-header ${expandedQuestion === questionId ? "expanded" : ""}`}
                          onClick={() => toggleQuestion(questionId)}
                        >
                          <span className="question-text">{item.q}</span>
                          {expandedQuestion === questionId ? (
                            <ChevronUp size={16} />
                          ) : (
                            <ChevronDown size={16} />
                          )}
                        </button>
                        {expandedQuestion === questionId && (
                          <div className="answer-content">
                            <p>{item.a}</p>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          ))}
        </div>
      </section>

      {/* Contact Support */}
      {/* <section className="help-section">
        <h2 className="section-title">
          <Mail size={18} />
          Still Need Help?
        </h2>

        {!showQueryForm && !querySubmitted && (
          <div className="contact-options">
            <button
              className="contact-btn email"
              onClick={() => setShowQueryForm(true)}
            >
              <MessageSquare size={20} />
              <div className="btn-content">
                <span className="btn-title">Submit a Query</span>
                <span className="btn-desc">We'll respond within 24 hours</span>
              </div>
            </button>
            <a href="mailto:support@rfconference.com" className="contact-btn">
              <Mail size={20} />
              <div className="btn-content">
                <span className="btn-title">Email Support</span>
                <span className="btn-desc">support@rfconference.com</span>
              </div>
            </a>
          </div>
        )}

        {showQueryForm && !querySubmitted && (
          <form className="query-form" onSubmit={handleQuerySubmit}>
            <div className="form-group">
              <label htmlFor="subject">Subject</label>
              <input
                type="text"
                id="subject"
                value={query.subject}
                onChange={(e) =>
                  setQuery({ ...query, subject: e.target.value })
                }
                placeholder="Brief description of your issue"
                disabled={querySubmitting}
              />
            </div>
            <div className="form-group">
              <label htmlFor="message">Message</label>
              <textarea
                id="message"
                value={query.message}
                onChange={(e) =>
                  setQuery({ ...query, message: e.target.value })
                }
                placeholder="Please describe your issue in detail..."
                rows={5}
                disabled={querySubmitting}
              />
            </div>
            <div className="form-actions">
              <button
                type="button"
                className="cancel-btn"
                onClick={() => setShowQueryForm(false)}
                disabled={querySubmitting}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="submit-btn"
                disabled={querySubmitting}
              >
                {querySubmitting ? (
                  "Sending..."
                ) : (
                  <>
                    <Send size={16} />
                    Send Query
                  </>
                )}
              </button>
            </div>
          </form>
        )}

        {querySubmitted && (
          <div className="query-success">
            <div className="success-icon">
              <CheckCircle size={48} />
            </div>
            <h3>Query Submitted!</h3>
            <p>
              We've received your query and will respond within 24 hours. Check
              your email for updates.
            </p>
            <button className="new-query-btn" onClick={handleNewQuery}>
              Submit Another Query
            </button>
          </div>
        )}
      </section> */}

      {/* App Info */}
      <div className="app-info">
        <p>RF Conference App v1.0.0</p>
        <p>For urgent matters, please visit the Help Desk at the venue.</p>
      </div>
    </div>
  );
}

export default HelpSupport;
